import React, { useEffect, useState } from "react";
import { api, errorMessage } from "../../lib/api";
import { formatTag } from "../../lib/format";
import { toast } from "sonner";
import { Crown, Download, Eye, Mail } from "lucide-react";
import { InfoSection, KV, LinkChip, AuthedDocLink } from "./shared";

export default function SubmissionsPanel() {
  const [round, setRound] = useState(1);
  const [subs, setSubs] = useState([]);
  const [aggregate, setAggregate] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [viewing, setViewing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/submissions/r${round}`);
      setSubs(data.submissions);
      setAggregate(data.prize_usage_aggregate || []);
      setSelectedIds(new Set());
    } catch (err) { toast.error(errorMessage(err)); }
  };
  useEffect(() => { load(); }, [round]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (tid) => {
    const n = new Set(selectedIds);
    n.has(tid) ? n.delete(tid) : n.add(tid);
    setSelectedIds(n);
  };

  const bulkShortlist = async () => {
    if (selectedIds.size === 0) return toast.error("Select teams first");
    if (!window.confirm(`Shortlist ${selectedIds.size} team(s) and email their leaders?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/shortlist", { team_ids: [...selectedIds] });
      toast.success(`Shortlisted ${data.shortlisted} team(s)`);
      await load();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  const markFinalists = async () => {
    if (selectedIds.size === 0) return toast.error("Select teams first");
    if (!window.confirm(`Mark ${selectedIds.size} team(s) as Finalists? (Leaders are notified only when results are published.)`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/finalists", { team_ids: [...selectedIds] });
      toast.success(`${data.finalists} team(s) marked as Finalist`);
      await load();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  const notifyUnselected = async () => {
    if (!window.confirm("Email all teams that are still 'registered' (not shortlisted)?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/notify-not-shortlisted");
      toast.success(`Notified ${data.notified} team(s)`);
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  const exportCsv = async () => {
    try {
      const resp = await api.get(`/admin/submissions/export-csv?round=${round}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `ideathon_round${round}_submissions.csv`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const exportFullCsv = async () => {
    try {
      const resp = await api.get(`/admin/export/full-csv`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `ideathon_full_export.csv`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const exportAllEditionsCsv = async () => {
    try {
      const resp = await api.get(`/admin/export/full-csv`, { params: { scope: "all" }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `ideathon_full_export_ALL_editions.csv`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const exportParticipantsCsv = async (allEditions = false) => {
    try {
      const params = allEditions ? { scope: "all" } : {};
      const resp = await api.get(`/admin/export/participants-csv`, { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = allEditions ? `ideathon_participants_ALL_editions.csv` : `ideathon_participants.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(errorMessage(err)); }
  };

  return (
    <div data-testid="submissions-panel">
      <div className="flex flex-wrap gap-3 items-center justify-between mb-4">
        <div className="flex border border-white/10">
          <button onClick={() => setRound(1)} className={`px-4 py-2 font-mono text-xs uppercase tracking-widest ${round === 1 ? "bg-brand text-black" : "text-gray-400 hover:text-white"}`} data-testid="round1-tab">Round 1</button>
          <button onClick={() => setRound(2)} className={`px-4 py-2 font-mono text-xs uppercase tracking-widest ${round === 2 ? "bg-brand text-black" : "text-gray-400 hover:text-white"}`} data-testid="round2-tab">Round 2</button>
        </div>
        <div className="flex flex-wrap gap-2">
          {round === 1 && (
            <button onClick={bulkShortlist} disabled={busy || selectedIds.size === 0} className="btn-primary !py-2 !px-3 text-xs flex items-center gap-2" data-testid="bulk-shortlist-btn">
              <Crown className="w-3.5 h-3.5" /> Shortlist ({selectedIds.size})
            </button>
          )}
          {round === 1 && (
            <button onClick={notifyUnselected} disabled={busy} className="btn-secondary !py-2 !px-3 text-xs flex items-center gap-2" data-testid="notify-unselected-btn">
              <Mail className="w-3.5 h-3.5" /> Notify Not-Selected
            </button>
          )}
          {round === 2 && (
            <button onClick={markFinalists} disabled={busy || selectedIds.size === 0} className="btn-primary !py-2 !px-3 text-xs flex items-center gap-2" data-testid="mark-finalist-btn">
              <Crown className="w-3.5 h-3.5" /> Mark Finalist ({selectedIds.size})
            </button>
          )}
          <button onClick={exportCsv} className="btn-secondary !py-2 !px-3 text-xs flex items-center gap-2" data-testid="export-csv-btn">
            <Download className="w-3.5 h-3.5" /> Export Round {round}
          </button>
          <button onClick={exportFullCsv} className="btn-primary !py-2 !px-3 text-xs flex items-center gap-2" data-testid="export-full-csv-btn">
            <Download className="w-3.5 h-3.5" /> Export ALL data
          </button>
          <button onClick={exportAllEditionsCsv} className="btn-primary !py-2 !px-3 text-xs flex items-center gap-2" data-testid="export-all-editions-csv-btn">
            <Download className="w-3.5 h-3.5" /> All editions
          </button>
          <button onClick={() => exportParticipantsCsv(false)} className="btn-secondary !py-2 !px-3 text-xs flex items-center gap-2" data-testid="export-participants-csv-btn">
            <Download className="w-3.5 h-3.5" /> Participants
          </button>
          <button onClick={() => exportParticipantsCsv(true)} className="btn-secondary !py-2 !px-3 text-xs flex items-center gap-2" data-testid="export-participants-all-csv-btn">
            <Download className="w-3.5 h-3.5" /> Participants (All)
          </button>
        </div>
      </div>

      <div className="card-surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="label-mono text-left border-b border-white/10">
              <th className="py-3 pr-4 w-8"></th>
              <th className="py-3 pr-4">Team</th>
              <th className="py-3 pr-4">{round === 1 ? "Project Title" : "Repo"}</th>
              <th className="py-3 pr-4">Status</th>
              <th className="py-3 pr-4">Team Status</th>
              <th className="py-3 pr-4">Avg Score</th>
              <th className="py-3 pr-4">Submitted</th>
              <th className="py-3 pr-4"></th>
            </tr>
          </thead>
          <tbody>
            {subs.map(s => (
              <tr key={s.id} className="border-b border-white/5" data-testid={`sub-row-${s.id}`}>
                <td className="py-3 pr-4">
                  <input type="checkbox" checked={selectedIds.has(s.team_id)} onChange={() => toggle(s.team_id)} className="accent-[#FF6B2B]"
                    disabled={s.status !== "submitted" || (round === 1 && s.team_status === "shortlisted") || (round === 2 && s.team_status === "finalist")}
                    data-testid={`select-${s.team_id}`} />
                </td>
                <td className="py-3 pr-4 text-white">{s.team_name}</td>
                <td className="py-3 pr-4 text-gray-400 max-w-xs truncate">{round === 1 ? (s.project_title || "—") : (s.repo_link || "—")}</td>
                <td className="py-3 pr-4"><span className={`badge ${s.status === "submitted" ? "badge-accepted" : "badge-invited"}`}>{s.status}</span></td>
                <td className="py-3 pr-4"><span className={`badge badge-${s.team_status || "upcoming"}`}>{s.team_status || "—"}</span></td>
                <td className="py-3 pr-4" data-testid={`score-${s.team_id}`}>
                  {s.avg_score != null ? (
                    <span className="font-mono text-brand font-semibold">{s.avg_score}<span className="text-gray-500 text-xs"> /10 · {s.judges_count}j</span></span>
                  ) : <span className="text-gray-600 font-mono text-xs">not scored</span>}
                  <div className="mt-1">
                    {s.assigned_judges > 0 ? (
                      s.scoring_complete
                        ? <span className="badge badge-accepted !text-[10px]" data-testid={`scoring-status-${s.team_id}`}>Scoring Complete</span>
                        : <span className="badge badge-invited !text-[10px]" data-testid={`scoring-status-${s.team_id}`}>Scoring Incomplete ({s.judges_count}/{s.assigned_judges})</span>
                    ) : <span className="text-gray-600 font-mono text-[10px]">no judges assigned</span>}
                  </div>
                </td>
                <td className="py-3 pr-4 font-mono text-xs text-gray-500">{s.submitted_at ? new Date(s.submitted_at).toLocaleString() : "—"}</td>
                <td className="py-3 pr-4"><button onClick={() => setViewing(s)} className="text-gray-400 hover:text-brand"><Eye className="w-4 h-4" /></button></td>
              </tr>
            ))}
            {subs.length === 0 && <tr><td colSpan={8} className="text-center text-gray-500 py-8">No submissions yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {round === 2 && aggregate.length > 0 && (
        <div className="card-surface mt-4" data-testid="prize-usage-aggregate">
          <div className="label-mono mb-1">Prize money usage · vendor partnership report</div>
          <p className="text-xs text-gray-500 mb-4">How many teams selected each option across all Round 2 submissions.</p>
          <div className="space-y-2.5">
            {aggregate.map(a => (
              <div key={a.option} className="flex items-center gap-3" data-testid={`agg-${a.option}`}>
                <div className="w-52 flex-shrink-0 text-sm text-gray-300 truncate">{a.option}</div>
                <div className="flex-1 h-2.5 bg-white/5 overflow-hidden">
                  <div className="h-full bg-brand" style={{ width: `${Math.min(100, (a.count / aggregate[0].count) * 100)}%` }} />
                </div>
                <div className="w-24 flex-shrink-0 text-right font-mono text-xs text-brand">{a.count} {a.count === 1 ? "team" : "teams"}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {viewing && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-5 overflow-y-auto" onClick={() => setViewing(null)} data-testid="submission-modal">
          <div className="card-surface max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4 mb-1">
              <div>
                <div className="label-mono">Round {round} · Submission detail</div>
                <h3 className="font-display text-3xl text-white mt-1">{round === 1 ? (viewing.project_title || "Untitled") : (viewing.team_name || "Round 2 submission")}</h3>
                <div className="text-sm text-gray-400 mt-1">by <span className="text-brand">{viewing.team_name}</span> · {viewing.team_status || "—"} · {viewing.status}</div>
              </div>
              <button onClick={() => setViewing(null)} className="text-gray-400 hover:text-white text-sm" data-testid="close-submission-modal">Close ✕</button>
            </div>

            {round === 1 ? <SubmissionDetailR1 s={viewing} /> : <SubmissionDetailR2 s={viewing} />}

            <details className="mt-6">
              <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-300 font-mono">// View raw JSON</summary>
              <pre className="mt-2 text-xs text-gray-400 whitespace-pre-wrap break-words font-mono bg-[#080C18] p-3 max-h-60 overflow-y-auto select-text">{JSON.stringify(viewing, null, 2)}</pre>
            </details>
          </div>
        </div>
      )}
    </div>
  );
}

function SubmissionDetailR1({ s }) {
  return (
    <div className="mt-6 border-t border-white/10 pt-6">
      <InfoSection title="Problem statement">{s.problem_statement}</InfoSection>
      <InfoSection title="Proposed solution">{s.proposed_solution}</InfoSection>

      <div className="mt-6 grid sm:grid-cols-2 gap-5">
        <KV k="Target audience">{s.target_audience}</KV>
        <KV k="Category">{s.category}</KV>
        <KV k="Innovation type">{formatTag(s.innovation_type)}</KV>
        <KV k="Idea stage">{formatTag(s.idea_stage)}</KV>
        <KV k="Estimated budget">{s.estimated_budget}</KV>
        <KV k="Market size">{s.market_size}</KV>
      </div>

      {(s.tech_stack && s.tech_stack.length > 0) && (
        <InfoSection title="Tech stack">
          <div className="flex flex-wrap gap-2">
            {s.tech_stack.map(t => (
              <span key={t} className="px-2.5 py-1 text-[11px] font-mono uppercase tracking-widest bg-white/5 border border-white/10 text-gray-300">{t}</span>
            ))}
          </div>
        </InfoSection>
      )}

      {s.team_skills && <InfoSection title="Team skills">{s.team_skills}</InfoSection>}
      {s.competitor_awareness && <InfoSection title="Competitors & differentiation">{s.competitor_awareness}</InfoSection>}

      <InfoSection title="Pitch video"><LinkChip href={s.pitch_video_link} label="Watch pitch video" /></InfoSection>
      {s.demo_link && <InfoSection title="Demo / mockup link"><LinkChip href={s.demo_link} /></InfoSection>}
      {s.supporting_doc_url && (
        <InfoSection title="Supporting document">
          <AuthedDocLink href={s.supporting_doc_url} label={s.supporting_doc_filename || "Open document"} />
          {s.supporting_doc_size ? <div className="text-xs text-gray-500 font-mono mt-1">{(s.supporting_doc_size / 1024).toFixed(1)} KB</div> : null}
        </InfoSection>
      )}

      <div className="mt-6 grid sm:grid-cols-2 gap-5 border-t border-white/5 pt-5">
        <KV k="Submitted at">{s.submitted_at ? new Date(s.submitted_at).toLocaleString() : "—"}</KV>
        <KV k="Submission ID"><span className="font-mono text-xs">{s.id}</span></KV>
      </div>
    </div>
  );
}

function SubmissionDetailR2({ s }) {
  const usage = s.prize_money_usage || [];
  return (
    <div className="mt-6 border-t border-white/10 pt-6">
      {s.problem_solved && <InfoSection title="Problem solved">{s.problem_solved}</InfoSection>}
      <div className="mt-6 grid sm:grid-cols-2 gap-5">
        <KV k="Prototype status">{s.prototype_status}</KV>
      </div>
      <InfoSection title="Repo link"><LinkChip href={s.repo_link} /></InfoSection>
      <InfoSection title="Live / demo link"><LinkChip href={s.demo_link} /></InfoSection>
      {s.pitch_video_link && <InfoSection title="Final pitch video"><LinkChip href={s.pitch_video_link} /></InfoSection>}

      {(s.tech_stack && s.tech_stack.length > 0) && (
        <InfoSection title="Tech stack">
          <div className="flex flex-wrap gap-2">
            {s.tech_stack.map(t => (
              <span key={t} className="px-2.5 py-1 text-[11px] font-mono uppercase tracking-widest bg-white/5 border border-white/10 text-gray-300">{t}</span>
            ))}
          </div>
        </InfoSection>
      )}

      {s.changes_from_r1 && <InfoSection title="Changes from Round 1">{s.changes_from_r1}</InfoSection>}
      {s.biggest_challenge && <InfoSection title="Biggest challenge">{s.biggest_challenge}</InfoSection>}
      {s.next_step_if_win && <InfoSection title="Next step if they win">{s.next_step_if_win}</InfoSection>}

      {usage.length > 0 && (
        <InfoSection title="Prize money usage">
          <div className="flex flex-wrap gap-2">
            {usage.map(u => (
              <span key={u} className="px-2.5 py-1 text-[11px] font-mono tracking-wide bg-brand/15 border border-brand/40 text-brand">{u}</span>
            ))}
          </div>
          {s.prize_money_usage_other && <div className="text-xs text-gray-400 mt-2">Other: {s.prize_money_usage_other}</div>}
        </InfoSection>
      )}

      {s.build_next && <InfoSection title="What they'd build next">{s.build_next}</InfoSection>}
      {Array.isArray(s.team_roles) && s.team_roles.length > 0 && (
        <InfoSection title="Team roles">
          <div className="flex flex-wrap gap-2">
            {s.team_roles.map((r, i) => (
              <span key={i} className="px-2.5 py-1 text-[11px] font-mono tracking-wide bg-white/5 border border-white/10 text-gray-300">
                <span className="text-white">{r.name}</span> · {r.role}
              </span>
            ))}
          </div>
        </InfoSection>
      )}

      <div className="mt-6 grid sm:grid-cols-2 gap-5 border-t border-white/5 pt-5">
        <KV k="Submitted at">{s.submitted_at ? new Date(s.submitted_at).toLocaleString() : "—"}</KV>
        <KV k="Submission ID"><span className="font-mono text-xs">{s.id}</span></KV>
      </div>
    </div>
  );
}
